"use client";

import { useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

interface NewsArticle {
  id: number;
  title: string;
  date: string;
  excerpt: string;
  category: string;
  readTime: string;
}

const tickerItems: string[] = [
"🏆 StanDave LLC ranked among Top 50 Global Logistics Innovators 2025",
"📦 New strategic partnership with leading Southeast Asian freight network announced",
"💼 StanDave expands Business Consulting division with 3 new senior advisors",
"🌍 MT103 SWIFT transaction processing now available in 42 additional countries",
"📈 Q1 2025 results: 38% average cost reduction achieved across client portfolio",
"🔐 StanDave achieves ISO 27001 certification for information security management",
"🚢 New import/export corridor launched between West Africa and Southeast Asia"];


const featuredNews = {
  category: "Partnerships",
  date: "March 5, 2025",
  author: "StanDave Editorial Desk",
  headline: "Partnership Deals Spark Global Attention with Agricultural Firms",
  lead: "StanDave LLC has entered a series of high-profile partnership negotiations with leading agricultural enterprises across multiple continents, drawing significant interest from institutional investors and industry stakeholders. The strategic deals are expected to reshape supply chain dynamics and open new corridors for asset management and logistics coordination."
};

const newsArticles: NewsArticle[] = [
{
  id: 1,
  title: "StanDave LLC Expands Global Logistics Network Across 42 New Countries",
  date: "March 3, 2025",
  excerpt:
  "StanDave LLC has officially extended its logistics and transportation services to 42 additional countries, strengthening its position as a premier global operations partner for businesses of all sizes.",
  category: "Expansion",
  readTime: "3 min read"
},
{
  id: 2,
  title: "Q1 2025 Performance Report: Record Client Cost Savings Achieved",
  date: "February 24, 2025",
  excerpt:
  "Our latest quarterly report reveals an average 38% cost reduction across our client portfolio, driven by data-driven asset management strategies and optimized supply chain solutions.",
  category: "Performance",
  readTime: "4 min read"
},
{
  id: 3,
  title: "New MT103 SWIFT Wire Transfer Service Now Live for Enterprise Clients",
  date: "February 15, 2025",
  excerpt:
  "StanDave's Global Transaction & Funding Solutions division has launched an enhanced MT103 SWIFT wire transfer service, enabling faster and more secure high-value international transactions.",
  category: "Services",
  readTime: "2 min read"
},
{
  id: 4,
  title: "Strategic Partnership with Southeast Asian Freight Network Announced",
  date: "February 8, 2025",
  excerpt:
  "We are proud to announce a landmark partnership with a leading Southeast Asian freight network, opening new trade corridors and reducing transit times for our import/export clients by up to 22%.",
  category: "Partnership",
  readTime: "3 min read"
},
{
  id: 5,
  title: "ISO 27001 Certification Achieved — Reinforcing Our Commitment to Data Security",
  date: "January 30, 2025",
  excerpt:
  "StanDave LLC has successfully obtained ISO 27001 certification, underscoring our unwavering commitment to protecting client data and maintaining the highest standards of information security.",
  category: "Compliance",
  readTime: "2 min read"
},
{
  id: 6,
  title: "Business Consulting Division Welcomes Three Senior Industry Advisors",
  date: "January 20, 2025",
  excerpt:
  "We are delighted to welcome three distinguished senior advisors to our Business Consulting & Financial Planning division, bringing decades of combined expertise in corporate strategy and global finance.",
  category: "Team",
  readTime: "2 min read"
}];


const categoryColors: Record<string, string> = {
  Expansion: "bg-[rgba(184,134,11,0.2)] text-gold border-[rgba(184,134,11,0.45)]",
  Performance: "bg-[rgba(52,211,153,0.12)] text-emerald-400 border-[rgba(52,211,153,0.3)]",
  Services: "bg-[rgba(96,165,250,0.12)] text-blue-400 border-[rgba(96,165,250,0.3)]",
  Partnership: "bg-[rgba(167,139,250,0.12)] text-violet-400 border-[rgba(167,139,250,0.3)]",
  Partnerships: "bg-[rgba(167,139,250,0.12)] text-violet-400 border-[rgba(167,139,250,0.3)]",
  Compliance: "bg-[rgba(184,134,11,0.2)] text-gold border-[rgba(184,134,11,0.45)]",
  Team: "bg-[rgba(184,134,11,0.2)] text-gold border-[rgba(184,134,11,0.45)]",
  "Breaking News": "bg-[rgba(239,68,68,0.15)] text-red-400 border-[rgba(239,68,68,0.35)]"
};

export default function CompanyNewsSection() {
  const tickerRef = useRef<HTMLDivElement>(null);
  const doubled = [...tickerItems, ...tickerItems];

  return (
    <section className="relative bg-[#0C1B33] pt-20">
      {/* Section label */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-gold rounded-full" />
          <span className="text-[11px] font-display font-600 uppercase tracking-[0.18em] text-gold">
            Company News
          </span>
        </div>
        <Link
          href="#news"
          className="flex items-center gap-1.5 text-[11px] font-display uppercase tracking-wider text-cream/40 hover:text-gold transition-colors duration-200">
          
          View All
          <Icon name="ArrowRightIcon" size={12} className="" />
        </Link>
      </div>

      {/* ── News Ticker ── */}
      <div className="relative z-10 border-y border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.04)] overflow-hidden">
        <div className="flex items-center">
          <div className="flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-gold z-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0C1B33] animate-pulse" />
            <span className="text-[10px] font-display font-700 uppercase tracking-[0.2em] text-[#0C1B33] whitespace-nowrap">
              LIVE NEWS
            </span>
          </div>
          <div className="overflow-hidden flex-1">
            <div
              ref={tickerRef}
              className="flex whitespace-nowrap"
              style={{ animation: "ticker-scroll 55s linear infinite" }}>
              
              {doubled.map((item, i) =>
              <span
                key={i}
                className="inline-flex items-center gap-3 px-8 text-[12px] font-body text-cream/70 hover:text-cream transition-colors duration-200 cursor-default">
                
                  {item}
                  <span className="text-gold/30 text-lg">|</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Featured News Banner (News of the Day) ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-2">
        <div className="relative overflow-hidden rounded-2xl border border-[rgba(184,134,11,0.45)] shadow-[0_8px_60px_rgba(0,0,0,0.5)]">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="/assets/images/cam_dubai_off1-1772754402700.png"
              alt="Business executives discussing renewable energy and investment deals in a Dubai high-rise boardroom"
              className="w-full h-full object-cover object-center" />
            
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1B33]/97 via-[#0C1B33]/85 to-[#0C1B33]/50" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C1B33]/80 via-transparent to-transparent" />
          </div>
          {/* Top gold bar */}
          <div className="relative h-1 w-full bg-gradient-to-r from-transparent via-gold to-transparent opacity-90" />
          {/* Radial glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 p-8 md:p-12">
            {/* Top meta row */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className={`text-[10px] font-display font-600 uppercase tracking-wider px-3 py-1.5 rounded-full border ${
              categoryColors[featuredNews.category] ?? categoryColors["Breaking News"]}`
              }>
                {featuredNews.category}
              </span>
              <div className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-gold/40" />
                <span className="text-[11px] font-body text-cream/45">{featuredNews.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-gold/40" />
                <span className="text-[11px] font-body text-cream/45">By {featuredNews.author}</span>
              </div>
              <div className="ml-auto hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-gold/10 border border-gold/25 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                <span className="text-[9px] font-display uppercase tracking-widest text-gold">News of the Day</span>
              </div>
            </div>

            {/* Headline */}
            <h2 className="font-display font-700 text-2xl md:text-3xl lg:text-4xl text-cream leading-tight tracking-tight mb-6 max-w-4xl">
              {featuredNews.headline}
            </h2>

            {/* Divider */}
            <div className="w-16 h-0.5 bg-gradient-to-r from-gold to-transparent mb-6" />

            {/* Lead paragraph */}
            <p className="text-[17px] md:text-[18px] font-body font-500 text-cream/85 leading-[1.75] max-w-3xl mb-8">
              {featuredNews.lead}
            </p>

            {/* CTA */}
            <div className="flex items-center gap-4">
              <button className="inline-flex items-center gap-2.5 px-6 py-3 bg-gold text-[#0C1B33] font-display text-sm uppercase tracking-widest rounded-sm hover:bg-gold/90 transition-all shadow-[0_4px_20px_rgba(201,168,76,0.3)] hover:shadow-[0_4px_30px_rgba(201,168,76,0.45)]">
                Read Full Story
                <Icon name="ArrowRightIcon" size={15} className="text-current" />
              </button>
              <span className="text-[11px] font-body text-cream/30">5 min read</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── News Cards Grid (text-only) ── */}
      <div id="news" className="relative z-10 max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsArticles.map((article) =>
          <article
            key={article.id}
            className="group relative flex flex-col rounded-xl border border-[rgba(201,168,76,0.12)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(201,168,76,0.04)] hover:border-[rgba(201,168,76,0.28)] transition-all duration-300 overflow-hidden">
            
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(201,168,76,0.4)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="p-6 flex flex-col flex-1">
                {/* Category badge + read time */}
                <div className="flex items-center justify-between mb-4">
                  <span
                  className={`text-[10px] font-display font-600 uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                  categoryColors[article.category] ??
                  "bg-[rgba(184,134,11,0.2)] text-gold border-[rgba(184,134,11,0.45)]"}`
                  }>
                  
                    {article.category}
                  </span>
                  <span className="text-[10px] font-body text-cream/30">{article.readTime}</span>
                </div>

                {/* Title */}
                <h3 className="font-display font-600 text-[17px] leading-snug text-cream group-hover:text-gold transition-colors duration-200 mb-3 line-clamp-2">
                  {article.title}
                </h3>

                {/* Excerpt */}
                <p className="text-[15px] font-body font-500 text-cream/75 leading-[1.75] line-clamp-3 flex-1">
                  {article.excerpt}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[rgba(255,255,255,0.05)]">
                  <div className="flex items-center gap-1.5">
                    <Icon name="CalendarIcon" size={12} className="text-gold/50" />
                    <span className="text-[11px] font-body text-cream/35">{article.date}</span>
                  </div>
                  <button className="flex items-center gap-1.5 text-[11px] font-display font-500 uppercase tracking-wider text-gold/60 group-hover:text-gold transition-colors duration-200">
                    Read More
                    <Icon
                    name="ArrowRightIcon"
                    size={12}
                    className="translate-x-0 group-hover:translate-x-0.5 transition-transform duration-200" />
                  
                  </button>
                </div>
              </div>
            </article>
          )}
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(201,168,76,0.2)] to-transparent" />

      {/* Ticker keyframe */}
      <style jsx>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>);

}